import {
    useState
} from "react";
import {
    useWindowDimensions,
    StyleSheet,
    ScrollView,
    View
} from "react-native";
import {
    BrandMark
} from "./brand";
import {
    NCoreUIKitTheme,
    Separator,
    TextInput,
    Button,
    Badge,
    Card,
    Chip,
    Icon,
    Item,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../i18n";
import {
    MessageSquare,
    ChevronRight,
    ArrowRight,
    CreditCard,
    Rocket,
    Search,
    Shield,
    Bell,
    Mail,
    Plug,
    User
} from "lucide-react-native";
import {
    createKitIcon
} from "../components/kitIcons";

const SearchIcon = createKitIcon(Search);

const ArrowRightIcon = createKitIcon(ArrowRight);

const RocketIcon = createKitIcon(Rocket);

const UserIcon = createKitIcon(User);

const CreditCardIcon = createKitIcon(CreditCard);

const ShieldIcon = createKitIcon(Shield);

const PlugIcon = createKitIcon(Plug);

const BellIcon = createKitIcon(Bell);

const MessageSquareIcon = createKitIcon(MessageSquare);

const MailIcon = createKitIcon(Mail);

const CATEGORIES = [
    {
        icon: RocketIcon,
        key: "start",
        count: 12
    },
    {
        icon: UserIcon,
        key: "account",
        count: 18
    },
    {
        icon: CreditCardIcon,
        key: "billing",
        count: 14
    },
    {
        icon: ShieldIcon,
        key: "security",
        count: 16
    },
    {
        key: "integrations",
        icon: PlugIcon,
        count: 21
    },
    {
        key: "notifications",
        icon: BellIcon,
        count: 9
    }
];

const ARTICLES = [
    {
        category: "account",
        key: "password",
        minutes: 2
    },
    {
        category: "start",
        key: "invite",
        minutes: 3
    },
    {
        category: "billing",
        key: "invoice",
        minutes: 2
    },
    {
        category: "security",
        key: "twoFactor",
        minutes: 4
    },
    {
        category: "billing",
        key: "plan",
        minutes: 3
    },
    {
        category: "integrations",
        key: "apiKey",
        minutes: 5
    }
];

const FOOTER_LINKS = [
    "privacy",
    "terms",
    "kvkk"
];

const POPULAR = [
    "password",
    "invoice",
    "invite",
    "twoFactor",
    "apiKey"
];

const stylesheet = StyleSheet.create({
    bar: {
        justifyContent: "space-between",
        alignItems: "center",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    chips: {
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    start: {
        alignSelf: "flex-start"
    },
    titleRow: {
        alignItems: "center",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    row: {
        alignItems: "center",
        flexDirection: "row"
    },
    page: {
        alignItems: "center",
        flexGrow: 1
    },
    wrap: {
        flexDirection: "row",
        flexWrap: "wrap"
    },
    cardRow: {
        flexDirection: "row"
    },
    center: {
        textAlign: "center"
    },
    cardCell: {
        minWidth: 0,
        flex: 1,
        flexShrink: 1
    },
    section: {
        width: "100%"
    }
});

const handlePress = () => undefined;

const Help01 = () => {
    const {
        t
    } = useDocsI18n();

    const {
        shapeConfigs,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        width
    } = useWindowDimensions();

    const [
        query,
        setQuery
    ] = useState("");

    const [
        inputKey,
        setInputKey
    ] = useState(0);

    const columns = width >= shapeConfigs.wideBreakpoint ? 3 : width >= shapeConfigs.compactBreakpoint ? 2 : 1;

    const isCompact = width < shapeConfigs.compactBreakpoint;

    const gutter = isCompact ? spaces.spacingMd : spaces.spacingLg;

    const normalizedQuery = query.trim().toLocaleLowerCase();

    const articles = ARTICLES.filter((article) => normalizedQuery === "" || t(`blocks.help01.article.${article.key}`).toLocaleLowerCase().includes(normalizedQuery));

    const rows = Array.from({
        length: Math.ceil(CATEGORIES.length / columns)
    }, (_, index) => CATEGORIES.slice(index * columns, (index + 1) * columns));

    const handlePopularPress = (key: string) => () => {
        setQuery(t(`blocks.help01.popular.${key}`));

        setInputKey((previous) => previous + 1);
    };

    const renderCategory = (category: typeof CATEGORIES[number]) => <Item
        accessibilityLabel={t(`blocks.help01.category.${category.key}.title`)}
        style={stylesheet.cardCell}
        onPress={handlePress}
        key={category.key}
        variant="outline"
    >
        <Item.Media
            icon={category.icon}
            variant="icon"
        />
        <Item.Content>
            <Item.Title>
                {t(`blocks.help01.category.${category.key}.title`)}
            </Item.Title>
            <Item.Description>
                {t(`blocks.help01.category.${category.key}.text`)}
            </Item.Description>
            <Text
                variant="bodySmallSize"
                color="mid"
            >
                {t("blocks.help01.articles", [
                    category.count
                ])}
            </Text>
        </Item.Content>
        <Item.Actions>
            <Icon
                icon={ChevronRight}
                color="mid"
            />
        </Item.Actions>
    </Item>;

    const renderRow = (items: typeof CATEGORIES, index: number) => <View
        style={[
            stylesheet.cardRow,
            {
                gap: spaces.spacingMd
            }
        ]}
        key={index}
    >
        {items.map(renderCategory)}
        {
            Array.from({
                length: columns - items.length
            }, (_, filler) => <View
                style={stylesheet.cardCell}
                key={filler}
            />)
        }
    </View>;

    const renderArticle = (article: typeof ARTICLES[number]) => <Item
        onPress={handlePress}
        key={article.key}
    >
        <Item.Content>
            <Item.Title>
                {t(`blocks.help01.article.${article.key}`)}
            </Item.Title>
            <Item.Description>
                {t("blocks.help01.articleMeta", [
                    t(`blocks.help01.category.${article.category}.title`),
                    article.minutes
                ])}
            </Item.Description>
        </Item.Content>
        <Item.Actions>
            <Icon
                icon={ChevronRight}
                color="mid"
            />
        </Item.Actions>
    </Item>;

    const renderFooterLink = (key: string) => <Button
        title={t(`blocks.help01.${key}`)}
        onPress={handlePress}
        variant="link"
        type="neutral"
        size="small"
        key={key}
    />;

    return <ScrollView
        contentContainerStyle={stylesheet.page}
    >
        <View
            style={[
                stylesheet.bar,
                stylesheet.section,
                {
                    paddingVertical: spaces.spacingSm,
                    paddingHorizontal: gutter,
                    gap: spaces.spacingMd
                }
            ]}
        >
            <View
                style={[
                    stylesheet.row,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <BrandMark />
                {
                    !isCompact && <Text
                        variant="bodyMediumSize"
                        color="mid"
                    >
                        {t("blocks.help01.centre")}
                    </Text>
                }
            </View>
            <View
                style={[
                    stylesheet.row,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                {
                    !isCompact && <>
                        <Button
                            title={t("blocks.help01.status")}
                            onPress={handlePress}
                            variant="ghost"
                            type="neutral"
                            size="small"
                        />
                        <Button
                            title={t("blocks.help01.changelog")}
                            onPress={handlePress}
                            variant="ghost"
                            type="neutral"
                            size="small"
                        />
                    </>
                }
                <Button
                    title={t("blocks.help01.backToApp")}
                    onPress={handlePress}
                    variant="outline"
                    type="neutral"
                    size="small"
                />
            </View>
        </View>
        <Separator />
        <View
            style={[
                stylesheet.section,
                {
                    maxWidth: shapeConfigs.wideBreakpoint + shapeConfigs.sideListWidth,
                    paddingVertical: spaces.spacingXxl,
                    paddingHorizontal: gutter,
                    gap: spaces.spacingXxl
                }
            ]}
        >
            <View
                style={[
                    stylesheet.section,
                    {
                        maxWidth: shapeConfigs.sheetMaxWidth,
                        gap: spaces.spacingMd,
                        alignSelf: "center"
                    }
                ]}
            >
                <Text
                    variant="displaySmallSize"
                    style={stylesheet.center}
                    role="heading"
                    color="high"
                >
                    {t("blocks.help01.heroTitle")}
                </Text>
                <Text
                    style={stylesheet.center}
                    variant="bodyLargeSize"
                    color="mid"
                >
                    {t("blocks.help01.heroLead")}
                </Text>
                <TextInput
                    accessibilityLabel={t("blocks.help01.searchLabel")}
                    placeholder={t("blocks.help01.searchPlaceholder")}
                    spreadBehaviour="stretch"
                    onChangeText={setQuery}
                    isClearEnabled={true}
                    initialValue={query}
                    icon={SearchIcon}
                    key={inputKey}
                    size="large"
                />
                <View
                    style={[
                        stylesheet.chips,
                        {
                            gap: spaces.spacingSm
                        }
                    ]}
                >
                    <Text
                        variant="labelMediumSize"
                        color="mid"
                    >
                        {t("blocks.help01.popularTitle")}
                    </Text>
                    {
                        POPULAR.map((key) => <Chip
                            title={t(`blocks.help01.popular.${key}`)}
                            onPress={handlePopularPress(key)}
                            variant="outline"
                            type="neutral"
                            key={key}
                        />)
                    }
                </View>
            </View>
            <View
                style={{
                    gap: spaces.spacingMd
                }}
            >
                <Text
                    variant="headlineSmallSize"
                    role="heading"
                    color="high"
                >
                    {t("blocks.help01.browse")}
                </Text>
                {rows.map(renderRow)}
            </View>
            <View
                style={[
                    stylesheet.section,
                    {
                        maxWidth: shapeConfigs.contentMaxWidth,
                        gap: spaces.spacingMd,
                        alignSelf: "center"
                    }
                ]}
            >
                <Text
                    variant="headlineSmallSize"
                    role="heading"
                    color="high"
                >
                    {t("blocks.help01.mostRead")}
                </Text>
                <Item.Group>
                    {articles.map(renderArticle)}
                </Item.Group>
                {
                    articles.length === 0 && <Text
                        variant="bodyMediumSize"
                        color="mid"
                    >
                        {t("blocks.help01.noResults")}
                    </Text>
                }
                <Button
                    title={t("blocks.help01.allArticles")}
                    style={stylesheet.start}
                    icon={ArrowRightIcon}
                    onPress={handlePress}
                    iconDirection="end"
                    variant="ghost"
                    type="neutral"
                    size="small"
                />
            </View>
            <Card
                variant="filled"
            >
                <Card.Header>
                    <Card.Title>
                        {t("blocks.help01.stillTitle")}
                    </Card.Title>
                    <Card.Description>
                        {t("blocks.help01.stillText")}
                    </Card.Description>
                </Card.Header>
                <Card.Content>
                    <View
                        style={[
                            stylesheet.wrap,
                            {
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        <Item
                            style={[
                                stylesheet.cardCell,
                                {
                                    flexBasis: shapeConfigs.sideListWidth
                                }
                            ]}
                            variant="outline"
                        >
                            <Item.Media
                                icon={MessageSquareIcon}
                                variant="icon"
                            />
                            <Item.Content>
                                <View
                                    style={[
                                        stylesheet.titleRow,
                                        {
                                            gap: spaces.spacingSm
                                        }
                                    ]}
                                >
                                    <Item.Title>
                                        {t("blocks.help01.liveTitle")}
                                    </Item.Title>
                                    <Badge
                                        title={t("blocks.help01.online")}
                                        variant="tonal"
                                        type="success"
                                        size="small"
                                    />
                                </View>
                                <Item.Description>
                                    {t("blocks.help01.liveText")}
                                </Item.Description>
                            </Item.Content>
                            <Item.Actions>
                                <Button
                                    title={t("blocks.help01.startChat")}
                                    onPress={handlePress}
                                    size="small"
                                />
                            </Item.Actions>
                        </Item>
                        <Item
                            style={[
                                stylesheet.cardCell,
                                {
                                    flexBasis: shapeConfigs.sideListWidth
                                }
                            ]}
                            variant="outline"
                        >
                            <Item.Media
                                icon={MailIcon}
                                variant="icon"
                            />
                            <Item.Content>
                                <Item.Title>
                                    {t("blocks.help01.emailTitle")}
                                </Item.Title>
                                <Item.Description>
                                    {t("blocks.help01.emailText")}
                                </Item.Description>
                            </Item.Content>
                            <Item.Actions>
                                <Button
                                    title={t("blocks.help01.emailAddress")}
                                    onPress={handlePress}
                                    variant="outline"
                                    type="neutral"
                                    size="small"
                                />
                            </Item.Actions>
                        </Item>
                    </View>
                </Card.Content>
            </Card>
        </View>
        <Separator />
        <View
            style={[
                stylesheet.bar,
                stylesheet.section,
                {
                    maxWidth: shapeConfigs.wideBreakpoint + shapeConfigs.sideListWidth,
                    paddingVertical: spaces.spacingLg,
                    paddingHorizontal: gutter,
                    gap: spaces.spacingSm
                }
            ]}
        >
            <Text
                variant="bodySmallSize"
                color="mid"
            >
                {t("blocks.help01.footer")}
            </Text>
            <View
                style={[
                    stylesheet.titleRow,
                    {
                        gap: spaces.spacingXs
                    }
                ]}
            >
                {FOOTER_LINKS.map(renderFooterLink)}
                <Badge
                    title={t("blocks.help01.allOperational")}
                    variant="tonal"
                    type="success"
                    size="small"
                />
            </View>
        </View>
    </ScrollView>;
};

export default Help01;
